import { typedBoolean, getEnvVar } from "./utils";

export type LocaleHostDiagnostics = {
  urlHost: string;
  forwardedHost: string;
  hostHeader: string;
  url: string;
  LOCALE_MODE: string;
  domains: string[];
  candidateHosts: string[];
  matchedDomain: string | undefined;
  fellBackToDefault: boolean;
};

type ParsedLocaleEnv = ReturnType<typeof parseLocaleEnv>;

export type GetLocaleResult = {
  locale: string;
  localePath: (path: string) => string;
  langsOptions: string[];
  languages: ParsedLocaleEnv;
  hostDiagnostics?: LocaleHostDiagnostics;
};

export const ensureTrailingSlash = (pathOrUrl: string, excludedPaths: RegExp[] = []) => {
  const isAbsoluteUrl = /^https?:\/\//i.test(pathOrUrl);
  const url = new URL(pathOrUrl, "http://placeholder");
  const isExcludedPath =
    url.pathname === "/" ||
    url.pathname.endsWith("/") ||
    /\.[^/]+$/.test(url.pathname) ||
    excludedPaths.some((pattern) => pattern.test(url.pathname));

  if (!isExcludedPath) {
    url.pathname = `${url.pathname}/`;
  }

  return isAbsoluteUrl ? `${url.origin}${url.pathname}${url.search}${url.hash}` : `${url.pathname}${url.search}${url.hash}`;
};

export const pathLocaleHelper = (path: string, locale: string, ...args: string[]) => {
  if (/http?/.test(path)) return path;
  if (!locale) return path;
  const arr = [locale, ...path.split("/"), ...args].filter(typedBoolean);
  arr[0] === arr[1] && arr.shift();
  return `/${arr.join("/")}`;
};

const parseLocaleEnv = () =>
  getEnvVar("LOCALE")
    .trim()
    .split(";")
    .map((i) => i.trim())
    .map((i) => {
      const [domain, locales] = i.split("|").map((j) => j.trim());
      const [country, langs] = locales.split("-").map((j) => j.trim());
      const langsOptions = langs.split("&").map((j) => j.trim());
      return {
        domain,
        country,
        langsOptions,
        defaultLang: langsOptions[0],
        locale: locales,
      };
    });

export const switchLocale = (locale: string) => {
  const locales = parseLocaleEnv();
  return locales.find((i) => i.locale === locale);
};

// WOV-1840: Normalize domain by stripping www. prefix and port for comparison.
// This prevents locale mismatches when the request host differs from the configured
// domain (e.g. "wovar.be" vs "www.wovar.be", or port variations on proxied requests).
const normalizeDomain = (domain: string) =>
  domain
    .replace(/^www\./i, "")
    .replace(/:\d+$/, "")
    .toLowerCase();

const findLocaleForHost = (
  locales: ReturnType<typeof parseLocaleEnv>,
  host: string
) => {
  const direct = locales.find((i) => i.domain === host);
  if (direct) return direct;
  const normalized = normalizeDomain(host);
  return locales.find((i) => normalizeDomain(i.domain) === normalized);
};

export function getLocale(
  request: Request,
  options?: { includeHostDiagnostics?: false | undefined }
): GetLocaleResult;
export function getLocale(
  request: Request,
  options: { includeHostDiagnostics: true }
): GetLocaleResult & { hostDiagnostics: LocaleHostDiagnostics };
export function getLocale(
  request: Request,
  options?: { includeHostDiagnostics?: boolean }
): GetLocaleResult {
  const locales = parseLocaleEnv();
  const localePath = (path: string) => path;
  const LOCALE_MODE = getEnvVar("LOCALE_MODE", false);

  // WOV-1840: Try multiple candidate hostnames in priority order. On Fly the
  // request.url host resolves to the internal *.fly.dev machine hostname rather
  // than the customer-facing wovar.xx domain — the original public hostname
  // only survives in x-forwarded-host (set by fly-proxy) and the raw host
  // header. Without checking those, every SSR request silently falls back to
  // nl-nl, putting carts on the NL channel regardless of the storefront the
  // customer actually visited.
  const urlHost = new URL(request.url).host;
  // x-forwarded-host can be a comma-separated chain when the request passes
  // through multiple proxies; the original public hostname is the first entry.
  const forwardedHost =
    request.headers.get("x-forwarded-host")?.split(",")[0]?.trim() ?? "";
  const hostHeader = request.headers.get("host") ?? "";
  const candidateHosts = [
    ...new Set([urlHost, forwardedHost, hostHeader].filter(Boolean)),
  ];

  let matchedLocale: ParsedLocaleEnv[number] | undefined;
  for (const candidate of candidateHosts) {
    matchedLocale = findLocaleForHost(locales, candidate);
    if (matchedLocale) break;
  }

  const fellBackToDefault = !matchedLocale || !LOCALE_MODE;
  const hostDiagnostics: LocaleHostDiagnostics | undefined = options?.includeHostDiagnostics
    ? {
        urlHost,
        forwardedHost,
        hostHeader,
        url: request.url,
        LOCALE_MODE,
        domains: locales.map((i) => i.domain),
        candidateHosts,
        matchedDomain: matchedLocale?.domain,
        fellBackToDefault,
      }
    : undefined;

  if (fellBackToDefault) {
    return {
      locale: "nl-nl",
      localePath,
      langsOptions: [],
      languages: locales,
      ...(hostDiagnostics ? { hostDiagnostics } : {}),
    };
  }
  const { country, defaultLang } = matchedLocale!;
  return {
    locale: [country, defaultLang].join("-"),
    localePath,
    langsOptions: matchedLocale!.langsOptions,
    languages: locales,
    ...(hostDiagnostics ? { hostDiagnostics } : {}),
  };
}
